/* eslint-disable max-len */

import chameleon from '@chamaeleonidae/chmln'
import ApiClient from 'api-client/ApiClient'
import { GlobalPreferences } from 'app/constants'
import store from 'app/store'
import { Route } from 'core/plugins/route'
import Bugsnag from 'utils/bugsnag'

const { preferenceStore } = ApiClient.getInstance()

export const getTrackingConfig = async () => {
  const response = await preferenceStore.getGlobalPreference(GlobalPreferences.Tracking)
  // Need to convert single quotes back to double quotes
  const config = JSON.parse(response.value)
  return config
}

export const updateTrackingConfig = async (config) =>
  preferenceStore.updateGlobalPreference(GlobalPreferences.Tracking, config)

export const isTrackingEnabled = async () => {
  try {
    const trackingConfig = await getTrackingConfig()
    return trackingConfig?.enableTracking
  } catch {
    return true
  }
}

// Data is sent to Amplitude using window.analytics

// name & body are both optional for both page & event
export const trackPage = (location) => {
  const route = Route.find(location)
  if (!route) {
    console.info(`No route found for '${location}'. Consider registering it as a Route.`)
    return
  }

  // Tracking for Amplitude
  if (window.amplitude) {
    const state = store.getState()
    const fqdn = window.location.origin
    const url = window.location.href
    const version = state?.session?.features?.releaseVersion
    if (route.tab) {
      const routeParams = route.pattern.match(location)
      const hashValue = routeParams[route.tab]
      window.amplitude.track(`Viewed "${route.name}:${hashValue}" Page`, {
        fqdn,
        url,
        appVersion: version,
      })
    } else {
      window.amplitude.track(`Viewed "${route.name}" Page`, {
        fqdn,
        url,
        appVersion: version,
      })
    }
  }

  // Tracking for Segment
  if (window.analytics) {
    if (route.tab) {
      const routeParams = route.pattern.match(location)
      const hashValue = routeParams[route.tab]
      window.analytics.page(`${route.name}:${hashValue}`)
    } else {
      window.analytics.page(route.name)
    }
  }

  Bugsnag.leaveBreadcrumb(route.name, {}, 'navigation')
}

export const trackEvent = (name, body = {}) => {
  if (window.amplitude) {
    const state = store.getState()
    const fqdn = window.location.origin
    const url = window.location.href
    const version = state?.session?.features?.releaseVersion
    window.amplitude.track(name, {
      ...body,
      fqdn,
      url,
      appVersion: version,
    })
  }
  if (!window.analytics) {
    return
  }

  window.analytics.track(name, body)
}

export const trackUpgrade = ({ username, customerTier }) => {
  trackEvent(`Upgrade from ${customerTier}`, { username, du: window.location.host })
}

const initiateAmplitudeSdk = () => {
  if (window.amplitude) {
    window.amplitude.init('fa5752e1df5fd0808e2fb0b1dbb8d21e', {
      fetchRemoteConfig: true,
      autocapture: false,
    })
  }
}

// Identify users with keystone id
export const setAmplitudeUser = (userId) => {
  try {
    if (window.amplitude) {
      const state = store.getState()
      const { role, email, displayName } = state?.session?.userDetails
      window.amplitude.setUserId(userId)
      const identifyEvent = new window.amplitude.Identify()
      identifyEvent.set('role', role)
      identifyEvent.set('email', email)
      identifyEvent.set('displayName', displayName)
      identifyEvent.set('screenWidth', window.screen.width)
      identifyEvent.set('screenHeight', window.screen.height)
      identifyEvent.set('viewportWidth', window.innerWidth)
      identifyEvent.set('viewportHeight', window.innerHeight)
      identifyEvent.set('devicePixelRatio', window.devicePixelRatio)
      window.amplitude.identify(identifyEvent)
    }
  } catch (e) {
    console.error('Error setting amplitude user', e)
  }
}

export const resetAmplitudeUser = () => {
  if (window.amplitude) {
    window.amplitude.reset()
  }
}

export const injectAmplitudeScriptContent = () => {
  if (!window.amplitude) {
    var t = document.createElement('script')
    t.type = 'text/javascript'
    t.async = !0
    t.onload = initiateAmplitudeSdk
    t.src = 'https://cdn.amplitude.com/script/fa5752e1df5fd0808e2fb0b1dbb8d21e.js'
    var r = document.getElementsByTagName('script')[0]
    r.parentNode.insertBefore(t, r)
  }
}

export const initiateChameleon = () => {
  chameleon.init('SakHXk7S99n2GDwO4QcJoJqMkYEoBX46HCeunqLobOgeNH-1VIDsW-GridbeEkglMmW8dr', {
    fastUrl: 'https://fast.chameleon.io/',
  })
}

export const setChameleonUser = (userId) => {
  const state = store.getState()
  const { role, email, displayName } = state?.session?.userDetails
  const version = state?.session?.features?.releaseVersion

  chameleon.identify(userId, {
    email,
    displayName,
    role,
    appVersion: version,
  })
}

// triggerRate is the percent from 0 to 100
export const trackChameleonEvent = (eventName, triggerRate = 100) => {
  if (Math.random() * 100 > triggerRate) {
    return
  }
  chameleon.track(eventName)
}

export const showChameleonFlow = (flowId, triggerRate = 100) => {
  if (Math.random() * 100 > triggerRate) {
    return
  }
  chameleon.show(flowId)
}

export const clarityScriptContent = `
(function(c,l,a,r,i,t,y){
    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
})(window, document, "clarity", "script", "sgyov5wfjy");
`

export const segmentScriptContent = `
"use strict";
 !function(){var i="analytics",analytics=window[i]=window[i]||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","screen","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware","register"];analytics.factory=function(e){return function(){if(window[i].initialized)return window[i][e].apply(window[i],arguments);var n=Array.prototype.slice.call(arguments);if(["track","screen","alias","group","page","identify"].indexOf(e)>-1){var c=document.querySelector("link[rel='canonical']");n.push({__t:"bpc",c:c&&c.getAttribute("href")||void 0,p:location.pathname,u:location.href,s:location.search,t:document.title,r:document.referrer})}n.unshift(e);analytics.push(n);return analytics}};for(var n=0;n<analytics.methods.length;n++){var key=analytics.methods[n];analytics[key]=analytics.factory(key)}analytics.load=function(key,n){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.setAttribute("data-global-segment-analytics-key",i);t.src="https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(t,r);analytics._loadOptions=n};analytics._writeKey="Fwpf5ncfE9AbSQwnuP88lclZTFByCdXy";;analytics.SNIPPET_VERSION="5.2.0";
  analytics.load("Fwpf5ncfE9AbSQwnuP88lclZTFByCdXy");
  analytics.page();
  }}();
`

export const appCuesSetAnonymous = () => {
  if (window.Appcues) {
    window.Appcues.anonymous()
  }
}
